import {
  AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, Output,
  Renderer2, SimpleChanges, ViewChild, ViewEncapsulation,
} from '@angular/core';
import {
  PREP_TOUR_DEFAULT_LABELS, PrepTourLabels, PrepTourPlacement, PrepTourProgressStyle, PrepTourStep, PrepTourStepAction,
  prepTourText,
} from './tour.types';

type Side = Exclude<PrepTourPlacement, 'auto'>;

interface CoachmarkButton {
  fill: 'solid' | 'outline' | 'flat';
  label: string;
  cls: string;
  click: () => void;
  icon?: string;
  disabled?: boolean;
  /** Flat buttons without the primary colour (the muted Skip). */
  muted?: boolean;
}

const GAP = 12;
const EDGE = 8;

/** Pick the side with room for a `w`×`h` card; honour `wanted` when it fits. */
function resolveSide(wanted: PrepTourPlacement, r: DOMRect | null, w: number, h: number): Side {
  if (!r || typeof window === 'undefined') return wanted === 'auto' ? 'bottom' : wanted;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const room: Record<Side, number> = { bottom: vh - r.bottom, top: r.top, right: vw - r.right, left: r.left };
  const need: Record<Side, number> = { bottom: h + GAP, top: h + GAP, right: w + GAP, left: w + GAP };
  if (wanted !== 'auto' && room[wanted] >= need[wanted]) return wanted;
  const order: Side[] = ['bottom', 'right', 'top', 'left'];
  const fits = order.filter((s) => room[s] >= need[s]);
  if (fits.length) return fits.sort((a, b) => room[b] / need[b] - room[a] / need[a])[0];
  return order.sort((a, b) => room[b] - room[a])[0];
}

let cardSeq = 0;

/**
 * The step card. Positioned in viewport coordinates next to the anchor rect
 * (side chosen for room, then clamped to the viewport), or centred when the
 * step has no target. `role="dialog" aria-modal="false"`: the page stays
 * operable. Re-rendered by the outlet whenever its inputs change.
 */
@Component({
  selector: 'prep-tour-coachmark',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'prep-tour-coachmark' },
  template: `
    <div #card class="prep-tour-card" [class.prep-tour-card--hero]="hero" [class.prep-tour-card--centred]="!anchor"
      role="dialog" aria-modal="false" [attr.aria-labelledby]="ids.title" [attr.aria-describedby]="ids.body">
      <div class="prep-tour-card__head">
        <span class="prep-tour-card__eyebrow">{{ hero ? '' : stepLabel }}</span>
        <span class="prep-tour-card__head-actions">
          <button *ngIf="step.minimizable" type="button" mat-icon-button class="prep-tour-card__minimize"
            [attr.aria-label]="labels.minimize" [attr.title]="labels.minimize" (click)="minimize.emit()">
            <mat-icon aria-hidden="true">remove</mat-icon>
          </button>
          <button type="button" mat-icon-button class="prep-tour-card__close"
            [attr.aria-label]="labels.close" [attr.title]="labels.close" (click)="close.emit()">
            <mat-icon aria-hidden="true">close</mat-icon>
          </button>
        </span>
      </div>
      <div *ngIf="step.icon" class="prep-tour-card__icon" aria-hidden="true"><mat-icon>{{ step.icon }}</mat-icon></div>
      <h2 class="prep-tour-card__title" [id]="ids.title">{{ titleText }}</h2>
      <p class="prep-tour-card__body" [id]="ids.body">{{ bodyText }}</p>
      <div *ngIf="actionNoteText && !missing" class="prep-tour-card__note" role="note">
        <span class="prep-tour-card__dot" [class.prep-tour-card__dot--pulse]="!reducedMotion" aria-hidden="true"></span>
        <span><strong>{{ labels.yourMove }}</strong> {{ actionNoteText }}</span>
      </div>
      <div *ngIf="busy; else footer" class="prep-tour-card__busy" role="status" aria-live="polite">
        <span class="prep-tour-card__spinner" aria-hidden="true"></span>
        <span>{{ labels.working }}</span>
      </div>
      <ng-template #footer>
        <div *ngIf="step.buttons !== false" class="prep-tour-card__footer"
          [class.prep-tour-card__footer--between]="progress !== 'none' && !hero"
          [class.prep-tour-card__footer--center]="hero">
          <prep-tour-progress *ngIf="progress !== 'none' && !hero" [index]="index" [count]="count" [variant]="progress"
            [text]="labels.counter(index + 1, count)" [ariaLabel]="labels.stepOf(index + 1, count)"></prep-tour-progress>
          <div class="prep-tour-card__buttons" [class.prep-tour-card__buttons--center]="hero">
            <ng-container *ngFor="let b of buttons">
              <ng-container [ngSwitch]="b.fill">
                <button *ngSwitchCase="'solid'" type="button" mat-flat-button color="primary" [ngClass]="b.cls"
                  [disabled]="b.disabled" (click)="b.click()"><mat-icon *ngIf="b.icon" class="prep-tour-card__btn-icon" aria-hidden="true">{{ b.icon }}</mat-icon>{{ b.label }}</button>
                <button *ngSwitchCase="'outline'" type="button" mat-stroked-button color="primary" [ngClass]="b.cls"
                  [disabled]="b.disabled" (click)="b.click()"><mat-icon *ngIf="b.icon" class="prep-tour-card__btn-icon" aria-hidden="true">{{ b.icon }}</mat-icon>{{ b.label }}</button>
                <button *ngSwitchDefault type="button" mat-button [color]="b.muted ? null : 'primary'" [ngClass]="b.cls"
                  [disabled]="b.disabled" (click)="b.click()"><mat-icon *ngIf="b.icon" class="prep-tour-card__btn-icon" aria-hidden="true">{{ b.icon }}</mat-icon>{{ b.label }}</button>
              </ng-container>
            </ng-container>
            <span *ngIf="step.pager as pager" class="prep-tour-card__pager" role="group">
              <button type="button" mat-icon-button [disabled]="pager.index() <= 0"
                [attr.aria-label]="labels.previous" [attr.title]="labels.previous" (click)="page.emit('prev')">
                <mat-icon aria-hidden="true">chevron_left</mat-icon>
              </button>
              <span class="prep-tour-card__pager-text" aria-live="polite">{{ pagerText }}</span>
              <button type="button" mat-icon-button [disabled]="pager.index() >= pager.count() - 1"
                [attr.aria-label]="labels.nextItem" [attr.title]="labels.nextItem" (click)="page.emit('next')">
                <mat-icon aria-hidden="true">chevron_right</mat-icon>
              </button>
            </span>
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styles: [`
    .prep-tour-coachmark { display: contents; }
    .prep-tour-card {
      position: absolute; box-sizing: border-box; width: 320px; max-width: calc(100vw - 24px); pointer-events: auto;
      border-radius: 10px; background: #fff; color: #1e293b; padding: 14px 18px 16px;
      box-shadow: 0 8px 30px rgba(0,0,0,.3); left: 0; top: 0;
    }
    .prep-tour-card--hero { width: 460px; text-align: center; }
    .prep-tour-card--centred { left: 50% !important; top: 50% !important; transform: translate(-50%, -50%); }
    .prep-tour-card__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .prep-tour-card__eyebrow { font-size: 11px; font-weight: 500; letter-spacing: .08em; text-transform: uppercase; color: #64748b; }
    .prep-tour-card__head-actions { display: flex; align-items: center; margin-right: -8px; }
    .prep-tour-card__head-actions .mat-icon-button { width: 32px; height: 32px; line-height: 32px; }
    .prep-tour-card__icon {
      margin-top: 4px; display: inline-flex; width: 48px; height: 48px; align-items: center; justify-content: center;
      border-radius: 12px; background: #e8f0fe; color: #1976d2;
    }
    .prep-tour-card__icon .mat-icon { font-size: 30px; width: 30px; height: 30px; }
    .prep-tour-card--hero .prep-tour-card__icon { margin: 4px auto 0; display: flex; }
    .prep-tour-card__title { margin: 6px 0 0; font-size: 15px; line-height: 1.35; font-weight: 700; color: #0f172a; }
    .prep-tour-card--hero .prep-tour-card__title { font-size: 19px; margin-top: 12px; }
    .prep-tour-card__body { margin: 6px 0 0; font-size: 13px; line-height: 1.55; color: #334155; white-space: pre-line; }
    .prep-tour-card--hero .prep-tour-card__body { font-size: 14px; margin-top: 8px; }
    .prep-tour-card__note {
      margin-top: 10px; display: flex; align-items: flex-start; gap: 8px; border-radius: 6px; border: 1px solid #e2e8f0;
      border-left: 3px solid #1976d2; background: #f8fafc; padding: 8px 10px; font-size: 12px; line-height: 1.4; color: #1e293b; text-align: left;
    }
    .prep-tour-card__note strong { font-weight: 600; color: #0f172a; }
    .prep-tour-card__dot { margin-top: 5px; width: 8px; height: 8px; flex-shrink: 0; border-radius: 999px; background: #1976d2; }
    @keyframes prep-tour-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.6); opacity: .55; } }
    .prep-tour-card__dot--pulse { animation: prep-tour-pulse 1.6s infinite; }
    .prep-tour-card__busy { margin-top: 14px; display: flex; align-items: center; gap: 8px; font-size: 12px; color: #475569; }
    .prep-tour-card__spinner {
      display: inline-block; width: 14px; height: 14px; flex-shrink: 0; border-radius: 999px;
      border: 2px solid #cbd5e1; border-top-color: #1976d2; animation: prep-tour-spin .8s linear infinite;
    }
    @keyframes prep-tour-spin { to { transform: rotate(360deg); } }
    .prep-tour-card__footer { margin-top: 14px; display: flex; align-items: center; gap: 12px; justify-content: flex-end; }
    .prep-tour-card__footer--between { justify-content: space-between; }
    .prep-tour-card__footer--center { justify-content: center; }
    .prep-tour-card__buttons { display: flex; min-width: 0; flex: 1 1 auto; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 6px; }
    .prep-tour-card__buttons--center { justify-content: center; }
    .prep-tour-card__buttons .mat-button-base { line-height: 32px; font-size: 13px; }
    .prep-tour-card__btn-icon { font-size: 18px; width: 18px; height: 18px; vertical-align: text-bottom; margin-right: 4px; }
    .prep-tour-card__skip--muted { color: #64748b; }
    .prep-tour-card__pager { margin-left: auto; display: flex; align-items: center; gap: 2px; padding-left: 8px; }
    .prep-tour-card__pager .mat-icon-button { width: 32px; height: 32px; line-height: 32px; }
    .prep-tour-card__pager-text { min-width: 3.5rem; text-align: center; font-size: 12px; color: #475569; font-variant-numeric: tabular-nums; }
    .prep-tour-card button:focus-visible { outline: 2px solid #1976d2; outline-offset: 2px; }
    @media (prefers-reduced-motion: reduce) {
      .prep-tour-card__dot--pulse { animation: none; }
      .prep-tour-card__spinner { animation: none; border-top-color: #cbd5e1; }
    }
  `],
})
export class PrepTourCoachmarkComponent implements OnChanges, AfterViewChecked {
  @Input() step!: PrepTourStep;
  @Input() index = 0;
  @Input() count = 1;
  /** Element to anchor to; null → centred. */
  @Input() anchor: HTMLElement | null = null;
  /** Anchor rect (viewport), for placement. */
  @Input() rect: DOMRect | null = null;
  /** Element that receives focus with `focus: 'target'` (defaults to the anchor). */
  @Input() focusAnchor: HTMLElement | null = null;
  @Input() canNext = true;
  @Input() skippable = true;
  /** Anchor is missing and the step explains that instead of its body. */
  @Input() missing = false;
  @Input() reducedMotion = false;
  @Input() labels: PrepTourLabels = PREP_TOUR_DEFAULT_LABELS;
  @Input() progress: PrepTourProgressStyle = 'dots';
  /** Card header text when the counter carries the position (e.g. the guide title). */
  @Input() heading = '';
  /** Bumped by the outlet to force a re-read of reactive texts. */
  @Input() tick = 0;

  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() skip = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() minimize = new EventEmitter<void>();
  @Output() page = new EventEmitter<'prev' | 'next'>();

  @ViewChild('card') private cardRef?: ElementRef<HTMLElement>;

  readonly ids = (() => {
    const n = cardSeq++;
    return { title: `prep-tour-cm-title-${n}`, body: `prep-tour-cm-body-${n}` };
  })();

  // Derived view values (recomputed on every input change)
  hero = false;
  actions: PrepTourStepAction[] = [];
  /** Footer buttons in order: Back · actions · Next/Finish · Skip. */
  buttons: CoachmarkButton[] = [];
  titleText = '';
  bodyText = '';
  actionNoteText = '';
  nextLabel = '';
  stepLabel = '';
  pagerText = '';
  busy = false;
  waitingNext = false;
  showSkip = false;

  private lastPos = '';

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.recompute();
    if (changes['step'] || changes['index']) this.moveFocus();
    if (changes['rect'] || changes['anchor'] || changes['step']) this.lastPos = '';
  }

  ngAfterViewChecked(): void {
    this.position();
  }

  private recompute(): void {
    const s = this.step;
    if (!s) return;
    this.hero = !!s.hero;
    this.actions = s.action === undefined ? [] : Array.isArray(s.action) ? s.action : [s.action];
    this.titleText = prepTourText(s.title);
    this.bodyText = this.missing ? (s.missingText || this.labels.missing) : prepTourText(s.body);
    this.actionNoteText = prepTourText(s.actionNote);
    this.nextLabel = prepTourText(s.nextLabel) || (this.index >= this.count - 1 && !s.next ? this.labels.finish : this.labels.next);
    this.stepLabel = s.label || (this.progress !== 'dots' && this.heading ? this.heading : this.labels.stepOf(this.index + 1, this.count));
    const p = s.pager;
    this.pagerText = p ? (p.label ? p.label() : `${p.index() + 1} / ${p.count()}`) : '';
    try { this.busy = !!(s.busy && s.busy()); } catch { this.busy = false; }
    this.waitingNext = !this.canNext && this.skippable === false && !this.missing;
    const skipTo = typeof s.skipTo === 'function' ? s.skipTo() : s.skipTo;
    this.showSkip = (!this.canNext && this.skippable && !skipTo) || (!!skipTo && !this.missing);
    this.buttons = this.buildButtons(s);
  }

  private buildButtons(s: PrepTourStep): CoachmarkButton[] {
    const out: CoachmarkButton[] = [];
    if (this.index > 0 && !this.hero && s.backButton !== false) {
      out.push({ fill: 'flat', label: this.labels.back, cls: 'prep-tour-card__back', click: () => this.back.emit() });
    }
    this.actions.forEach((act, i) => {
      let hidden = false;
      try { hidden = !!(act.hidden && act.hidden()); } catch { hidden = false; }
      if (hidden) return;
      const primary = !this.canNext && i === 0;
      out.push({
        fill: act.fillMode || (this.canNext ? 'outline' : 'solid'),
        label: prepTourText(act.label),
        icon: prepTourText(act.icon) || undefined,
        cls: 'prep-tour-card__action' + (primary ? ' prep-tour-card__primary' : ''),
        click: () => { void act.run(); },
      });
    });
    if (s.nextButton !== false) {
      if (this.canNext) {
        out.push({ fill: s.nextFillMode || 'solid', label: this.nextLabel, cls: 'prep-tour-card__primary', click: () => this.next.emit() });
      } else if (this.waitingNext) {
        out.push({ fill: 'solid', label: this.nextLabel, cls: 'prep-tour-card__next-waiting', disabled: true, click: () => {} });
      }
    }
    if (this.showSkip) {
      const outline = s.skipFillMode === 'outline';
      const primary = !this.canNext && !this.actions.length;
      out.push({
        fill: outline ? 'outline' : 'flat',
        label: s.skipLabel || this.labels.skipStep,
        cls: 'prep-tour-card__skip' + (primary ? ' prep-tour-card__primary' : '') + (outline ? '' : ' prep-tour-card__skip--muted'),
        muted: !outline,
        click: () => this.skip.emit(),
      });
    }
    return out;
  }

  /** Keyboard users operate the guide from the card: focus its primary control on every step. */
  private moveFocus(): void {
    const step = this.step;
    const target = this.focusAnchor || this.anchor;
    setTimeout(() => {
      if (step.focus === 'target' && target) {
        const el = target.matches('button, a[href], input, [tabindex]') ? target
          : (target.querySelector<HTMLElement>('button, a[href], input, [tabindex]') || target);
        if (el === target && !el.hasAttribute('tabindex') && !(el instanceof HTMLButtonElement)) el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
        return;
      }
      const btn = this.host.nativeElement.querySelector<HTMLElement>('.prep-tour-card__primary, .prep-tour-card__close');
      if (btn) btn.focus({ preventScroll: true });
    }, 0);
  }

  /** Place the card next to the anchor rect; clamp inside the viewport. */
  private position(): void {
    const card = this.cardRef ? this.cardRef.nativeElement : null;
    if (!card) return;
    const r = this.anchor ? this.rect : null;
    if (!r) {
      if (this.lastPos !== 'centre') {
        this.lastPos = 'centre';
        this.renderer.removeStyle(card, 'left');
        this.renderer.removeStyle(card, 'top');
      }
      return;
    }
    const w = card.offsetWidth || (this.hero ? 460 : 320);
    const h = card.offsetHeight || 240;
    const side = resolveSide(this.step.placement || 'auto', r, w, h);
    let left: number;
    let top: number;
    switch (side) {
      case 'top': left = r.left + r.width / 2 - w / 2; top = r.top - GAP - h; break;
      case 'left': left = r.left - GAP - w; top = r.top + r.height / 2 - h / 2; break;
      case 'right': left = r.right + GAP; top = r.top + r.height / 2 - h / 2; break;
      default: left = r.left + r.width / 2 - w / 2; top = r.bottom + GAP; break;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
    top = Math.max(EDGE, Math.min(top, vh - h - EDGE));
    const key = `${Math.round(left)}:${Math.round(top)}`;
    if (key === this.lastPos) return;
    this.lastPos = key;
    this.renderer.setStyle(card, 'left', `${Math.round(left)}px`);
    this.renderer.setStyle(card, 'top', `${Math.round(top)}px`);
  }
}
